import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

type CardVariant = 'default' | 'elevated' | 'outlined';
type CardPadding = 'sm' | 'md' | 'lg';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  children: ReactNode;
}

// Bento standard per docs/SEALED_CONVENTIONS.md §1:
//   - Outer page/section containers: rounded-3xl  → variant="elevated"
//   - Inner cards / module grid items: rounded-2xl → variant="default" | "outlined"
const VARIANT_CLASSES: Record<CardVariant, string> = {
  default: 'bg-white rounded-2xl shadow-soft',
  elevated: 'bg-white rounded-3xl shadow-elevated',
  outlined: 'bg-white rounded-2xl border border-stone',
};

const PADDING_CLASSES: Record<CardPadding, string> = {
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-6 md:p-8',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  function Card(
    { variant = 'default', padding = 'md', children, className = '', ...rest },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={`${VARIANT_CLASSES[variant]} ${PADDING_CLASSES[padding]} ${className}`}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
